'use client';

import React from 'react';
import { useAppDispatch } from '@/store/hooks';
import { setPlayerDetails } from '@/store/reducers/drawer-content-slice';
import { showDrawerState } from '@/store/reducers/ui-slice';
import { PlayerProfile } from '@alien-platform/bo-core';
import { Avatar, Button, styled, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { CurrencyEth } from '@phosphor-icons/react/dist/ssr/CurrencyEth';
import { EnvelopeSimple } from '@phosphor-icons/react/dist/ssr/EnvelopeSimple';
import { TelegramLogo } from '@phosphor-icons/react/dist/ssr/TelegramLogo';

import { GeneralDrawer } from '@/types/general';

import CopyButton from './copy-button';

const Root = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
});

const InfoColumn = styled(Stack)({
  flexDirection: 'column',
  width: '100%',
});

const UsernameRow = styled(Stack)({
  flexDirection: 'row',
  gap: '8px',
  alignItems: 'center',
  '&:hover .copy-button': {
    opacity: 1,
  },
});

const UsernameButton = styled(Button)({
  display: 'block',
  whiteSpace: 'nowrap',
  padding: '0 !important',
  maxWidth: '100%',
  textAlign: 'start',
});

const EllipsisText = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textAlign: 'start',
});

const InfoBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  maxWidth: '100%',
  '&:hover .copy-button': {
    opacity: 1,
  },
});

const CopyButtonWrapper = styled(Box)({
  opacity: 0,
  transition: 'opacity 0.1s ease-in-out',
});

const StyledCopyButton = styled(CopyButton)({
  opacity: 0,
  transition: 'opacity 0.1s ease-in-out',
});

interface PlayerDetails {
  playerCode: string;
  playerId?: string;
  profile?: PlayerProfile;
  isTest?: boolean;
  isVerified?: boolean;
  username?: string;
  avatar?: string;
}

interface PlayerCellProps extends PlayerDetails {
  showCopyButton?: boolean;
  showFilterButton?: boolean;
}

export function PlayerCell(player: PlayerCellProps): React.JSX.Element {
  const dispatch = useAppDispatch();
  const profile = player.profile;

  const handleClick = (e: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    dispatch(
      setPlayerDetails({
        playerCode: player.playerCode,
        activeList: [],
      })
    );
    dispatch(showDrawerState(GeneralDrawer.PLAYER_DETAILS));
  };

  return (
    <Root spacing={1}>
      <Avatar src={player.avatar} />
      <InfoColumn>
        <UsernameRow>
          <UsernameButton color="inherit" onClick={handleClick}>
            <EllipsisText variant="body2">{player.username}</EllipsisText>
          </UsernameButton>
          <CopyButtonWrapper className="copy-button">
            <StyledCopyButton text={player.username} />
          </CopyButtonWrapper>
        </UsernameRow>

        {profile?.email?.address ? (
          <InfoBox>
            <EnvelopeSimple />
            <EllipsisText color="text.secondary" variant="body2">
              {player?.profile?.email?.address}
            </EllipsisText>
            <CopyButtonWrapper className="copy-button">
              <CopyButton text={player?.profile?.email?.address} />
            </CopyButtonWrapper>
          </InfoBox>
        ) : profile?.metaMask?.walletAddress ? (
          <InfoBox>
            <CurrencyEth />
            <Typography color="text.secondary" variant="body2" sx={{ textAlign: 'start' }}>
              {player?.profile?.metaMask?.walletAddress}
            </Typography>
            <CopyButtonWrapper className="copy-button">
              <CopyButton text={player?.profile?.metaMask?.walletAddress} />
            </CopyButtonWrapper>
          </InfoBox>
        ) : profile?.telegram?.telegramId ? (
          <InfoBox>
            <TelegramLogo />
            <Typography color="text.secondary" variant="body2" sx={{ textAlign: 'start' }}>
              {player?.profile?.telegram?.telegramId}
            </Typography>
            <CopyButtonWrapper className="copy-button">
              <CopyButton text={player?.profile?.telegram?.telegramId} />
            </CopyButtonWrapper>
          </InfoBox>
        ) : null}
      </InfoColumn>
    </Root>
  );
}
